/**
 * ApiSettings — Modal to switch between Live and Dev API.
 * Stores the choice in localStorage. On apply, reloads the page.
 */

import React, { useState } from 'react'

const API_OPTIONS = [
  {
    id: 'live',
    label: 'Live API',
    url: 'https://api.ortoai.administer.cloud',
    description: 'Produktionsdaten — stabile Version',
    color: '#22c55e',
  },
  {
    id: 'dev',
    label: 'Dev API',
    url: 'https://api-dev.ortoai.administer.cloud',
    description: 'Entwicklungsdaten — neueste Features',
    color: '#f59e0b',
  },
]

const STORAGE_KEY = 'orto_api_override'
const PC_MODE_KEY = 'orto_pointcloud_mode'

/** Liest die gespeicherte API-Override URL (oder null für Default) */
export function getApiOverride(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

/** Liest den Pointcloud-Modus: online, local (HTTP-Server), oder public (App-Verzeichnis) */
export function getPointcloudMode(): 'online' | 'local' | 'public' {
  try {
    return (localStorage.getItem(PC_MODE_KEY) as 'online' | 'local' | 'public') || 'online'
  } catch {
    return 'online'
  }
}

interface Props {
  onClose: () => void
  onApply: () => void
}

export function ApiSettings({ onClose, onApply }: Props) {
  const current = getApiOverride()
  const [selected, setSelected] = useState<string>(
    current || API_OPTIONS[0].url
  )
  const [pcMode, setPcMode] = useState<'online' | 'local' | 'public'>(getPointcloudMode())

  const handleApply = () => {
    localStorage.setItem(STORAGE_KEY, selected)
    localStorage.setItem(PC_MODE_KEY, pcMode)
    onApply()
  }

  const handleReset = () => {
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(PC_MODE_KEY)
    onApply()
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.5)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff', borderRadius: 12, padding: 24,
          width: 420, maxWidth: '90vw',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>API-Einstellungen</h2>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#999', lineHeight: 1 }}
          >×</button>
        </div>

        <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
          Wählen Sie die API-Umgebung für die Datenquelle:
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
          {API_OPTIONS.map((opt) => {
            const isSelected = selected === opt.url
            return (
              <label
                key={opt.id}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 12,
                  padding: '12px 14px', borderRadius: 8, cursor: 'pointer',
                  border: isSelected ? `2px solid ${opt.color}` : '2px solid #e5e7eb',
                  background: isSelected ? `${opt.color}08` : '#fff',
                  transition: 'border-color 0.15s, background 0.15s',
                }}
              >
                <input
                  type="radio"
                  name="api"
                  value={opt.url}
                  checked={isSelected}
                  onChange={() => setSelected(opt.url)}
                  style={{ marginTop: 3 }}
                />
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: opt.color, display: 'inline-block',
                    }} />
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{opt.label}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{opt.description}</div>
                  <code style={{ fontSize: 11, color: '#999', marginTop: 4, display: 'block' }}>{opt.url}</code>
                </div>
              </label>
            )
          })}
        </div>

        {current && (
          <p style={{ fontSize: 11, color: '#999', marginBottom: 12 }}>
            Aktuell überschrieben: <code>{current}</code>
          </p>
        )}

        {/* Pointcloud-Modus */}
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16, marginBottom: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Punktwolken-Quelle</h3>
          <p style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
            Bei langsamer Internetverbindung können Punktwolken von einem lokalen Server geladen werden.
            Die lokalen Pfade werden im Admin pro Punktwolke konfiguriert.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {([
              { id: 'online' as const, label: 'Online (Server)', desc: 'Punktwolken vom Remote-Server laden', color: '#3b82f6' },
              { id: 'local' as const, label: 'Lokaler Server', desc: 'Punktwolken von einem lokalen HTTP-Server laden (z.B. localhost:8080)', color: '#f59e0b' },
              { id: 'public' as const, label: 'App-Verzeichnis (public/)', desc: 'Punktwolken aus dem public/-Ordner der React-App (per Symlink)', color: '#8b5cf6' },
            ]).map((opt) => {
              const isActive = pcMode === opt.id
              return (
                <label
                  key={opt.id}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 8,
                    padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                    border: isActive ? `2px solid ${opt.color}` : '2px solid #e5e7eb',
                    background: isActive ? `${opt.color}08` : '#fff',
                  }}
                >
                  <input type="radio" name="pcmode" checked={isActive} onChange={() => setPcMode(opt.id)} style={{ marginTop: 2 }} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{opt.label}</div>
                    <div style={{ fontSize: 11, color: '#888' }}>{opt.desc}</div>
                  </div>
                </label>
              )
            })}
          </div>
          {pcMode === 'local' && (
            <p style={{ fontSize: 11, color: '#f59e0b', marginTop: 8 }}>
              ⚠️ Starten Sie einen lokalen HTTP-Server: <code>cd ~/orto-pointclouds && python3 -m http.server 8080</code>
            </p>
          )}
          {pcMode === 'public' && (
            <div style={{ fontSize: 11, color: '#8b5cf6', marginTop: 8 }}>
              <p style={{ marginBottom: 4 }}>Erstellen Sie einen Symlink im <code>orto/public/</code>-Verzeichnis:</p>
              <code style={{ display: 'block', background: '#f5f3ff', padding: '6px 8px', borderRadius: 4, fontSize: 10, lineHeight: 1.5 }}>
                ln -s ~/orto-pointclouds/KHD_Aussen orto/public/pointclouds/KHD_Aussen
              </code>
              <p style={{ marginTop: 4 }}>Die Punktwolken sind dann unter <code>/pointclouds/KHD_Aussen/metadata.json</code> erreichbar.</p>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <button
            onClick={handleReset}
            style={{
              background: 'none', border: '1px solid #ddd', color: '#666',
              padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
            }}
          >
            Zurücksetzen (Standard)
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={onClose}
              style={{
                background: 'none', border: '1px solid #ddd', color: '#666',
                padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
              }}
            >
              Abbrechen
            </button>
            <button
              onClick={handleApply}
              style={{
                background: '#3b82f6', border: 'none', color: '#fff',
                padding: '8px 20px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
              }}
            >
              Übernehmen
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
